import React from 'react';
import { toast } from 'react-toastify';
import { useContextSelector } from 'use-context-selector';
import { RouterContext } from '../../contexts/RouterContext';
import { Filters, SpecialistContext } from '../../contexts/SpecialistContext';
import { FilterItems, SpecialistList as Specialist } from '../../models/Specialist';
import { Specialty } from '../../models/Specialty';
import {
  fetchSpecialistById,
  fetchSpecialists,
  fetchSpecialties,
  fetchSpecialtiesById,
  fetchUnitsFilter
} from '../../modules/specialists';
import { PAGES } from '../../Router/pages';
import { FirebaseEvents } from '../../utils/enums/firebase-events';
import { useAnalytics } from '../../hooks/useAnalytics';

type fetchSpecialistsProps = {
  name?: string;
  specialties?: string;
  specialty?: string;
  units?: string;
  area_expertise?: string;
  medical_field?: string;
  medical_indicator?: number;
};

export const useSpecialistList = () => {
  const [loading, setLoading] = React.useState(0);
  const [errorSearchProduct, setErrorSearchProduct] = React.useState('');
  const [showSearchWrapper, setShowSearchWrapper] = React.useState(false);
  const [modalFilterVisible, setModalFilterVisible] = React.useState(false);
  const [specialtyList, setSpecialtyList] = React.useState<Specialty[]>([]);
  const [medicalFields, setMedicalFields] = React.useState<FilterItems[]>([]);
  const [specialist, setSpecialist] = React.useState<Specialist | null>(null);
  const [areaExpertises, setAreaExpertises] = React.useState<FilterItems[]>([]);
  const [searchTherm, setSearchTherm] = React.useState('');

  const { goTo } = useContextSelector(RouterContext, state => state);

  const {
    specialties,
    setSpecialists: addSpecialistsStore,
    specialists,
    setSelectedSpecialist: setSelectedSpecialistStore,
    selectedSpecialist,
    selectedSpecialty,
    setSelectedSpecialty: setSpecialtyStore,
    setSpecialties,
    listUnitsFilter,
    setListUnitsFilter,
    findSpecialties,
    selectedFilters,
    handleCleanSelectedFilters,
    valueFilter,
    setValueFilter: addFilterStore
  } = useContextSelector(SpecialistContext, state => state);

  const getUnitsToFilter = async () => {
    setLoading(r => r + 1);

    const response = await fetchUnitsFilter();
    if (response.error) setErrorSearchProduct('Erro ao consultar unidades para filtro');

    if (!response.error)
      setListUnitsFilter(
        response.data.map(item => {
          const itemLowerCase = item.unit_name.toLowerCase();

          return {
            id: item.unit_id,
            name: itemLowerCase.replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase())
          };
        })
      );

    setLoading(r => r - 1);
  };

  const { logEvent } = useAnalytics();

  const getSpecialistsByName = async (name: string) => {
    setLoading(r => r + 1);

    logEvent({
      eventName: FirebaseEvents.schedule_specialist_search,
      eventParams: {
        search: name
      }
    });

    setSpecialtyStore(undefined);
    const params: fetchSpecialistsProps = {};

    if (name.length > 2) params.name = name;

    const response = await fetchSpecialists(params);

    if (response.error) {
      console.error(response.message);
      setErrorSearchProduct('Erro ao buscar especialista');
    }

    if (!response.error) {
      if (response?.data?.length > 0) {
        addSpecialistsStore(response.data);
      } else {
        setErrorSearchProduct('Não encontramos nenhum especialista.');

        addSpecialistsStore([]);
      }
    }

    setLoading(r => r - 1);
  };

  const buildFiltersToGetSpecialists = (props: { specialtyId: string; filters: Filters }) => {
    const { units, expertise, medicalField } = props.filters;
    return {
      specialty: props.specialtyId,
      medical_indicator: 1,
      ...(units.length > 0 ? { units: units.join(',') } : {}),
      ...(expertise.length > 0 ? { area_expertise: expertise[0] } : {}),
      ...(medicalField.length > 0 ? { medical_field: medicalField[0] } : {})
    };
  };

  const getSpecialistsByFilter = async (specialty: Specialty, filters: Filters) => {
    setLoading(r => r + 1);
    setSpecialtyList([specialty]);

    const response = await fetchSpecialists(buildFiltersToGetSpecialists({ specialtyId: specialty.id, filters }));

    if (response.error) setErrorSearchProduct('Erro ao consultar especialistas');

    if (!response.error) {
      addSpecialistsStore(response.data);
      setShowSearchWrapper(false);
    }
    setLoading(r => r - 1);
  };

  const openSpecialistDetails = async (specialist: Specialist) => {
    setLoading(r => r + 1);

    logEvent({
      eventName: FirebaseEvents.schedule_selected_specialist,
      eventParams: {
        specialist_id: specialist.id,
        specialist_name: specialist.name,
        search: searchTherm
      }
    });

    const result = await fetchSpecialistById(specialist.id);

    if (result.error) {
      toast.error('Não foi possível buscar o especialista.');
    }

    if (!result.error) {
      setSelectedSpecialistStore(result.data);
      goTo(PAGES.SPECIALIST_DETAILS.id, { previousPage: true });
    }
    setLoading(r => r - 1);
  };

  const onClickOnSpecialty = async (specialty: Specialty, filters?: Filters) => {
    logEvent({
      eventName: FirebaseEvents.schedule_selected_speciality,
      eventParams: {
        speciality_id: specialty.id,
        speciality_name: specialty.name
      }
    });

    let modiefiedFilters = filters;

    if (selectedSpecialty?.id !== specialty.id) {
      modiefiedFilters = {
        expertise: [],
        medicalField: [],
        units: []
      };
      handleCleanSelectedFilters();
    }

    await getSpecialistsByFilter(specialty, modiefiedFilters ?? selectedFilters);
    setSpecialtyStore(specialty);
    handleExpertiseAndMedicalField(specialty.id);
  };

  const handleClickListItem = async ({
    type,
    item,
    inputText,
    filters
  }: {
    type: 'SPECIALTY' | 'SPECIALIST';
    item: Specialist | Specialty;
    inputText: string;
    filters?: Filters;
  }) => {
    setLoading(r => r + 1);
    setShowSearchWrapper(false);

    if (type === 'SPECIALTY') await onClickOnSpecialty(item as Specialty, filters);
    if (type === 'SPECIALIST') await openSpecialistDetails(item as Specialist);

    addFilterStore(inputText);
    setLoading(r => r - 1);
  };

  const handleExpertiseAndMedicalField = async (specialtyId: string) => {
    const fetchResponse = await fetchSpecialtiesById(specialtyId);

    if (!fetchResponse.error) {
      const expertise = [] as FilterItems[];
      const medicalField = [] as FilterItems[];

      fetchResponse.data.area_expertise.forEach(item => {
        const checkIfExists = expertise.find(({ id }) => id === item.area_expertise?.id);

        if (!checkIfExists)
          expertise.push({ id: item?.area_expertise?.id ?? '', name: item?.area_expertise?.name ?? '' });
      });

      fetchResponse.data.medical_field.forEach(item => {
        const checkIfExists = medicalField.find(({ id }) => id === item.medical_field?.id);

        if (!checkIfExists)
          medicalField.push({ id: item?.medical_field?.id ?? '', name: item?.medical_field?.name ?? '' });
      });

      setAreaExpertises(expertise);
      setMedicalFields(medicalField);
    } else toast.error('Não foi possível buscar área de atuação.');
  };

  const getSpecialties = async () => {
    setLoading(r => r + 1);
    const result = await fetchSpecialties();
    if (result.error) toast.error('Não foi possível buscar as especialidades.');
    if (!result.error) {
      const sortResult = result.data?.sort((item1, item2) => item1.name.localeCompare(item2.name));
      setSpecialties(sortResult);
      setSpecialtyList(sortResult);
    }

    setLoading(r => r - 1);
  };

  React.useEffect(() => {
    if (!listUnitsFilter?.length) getUnitsToFilter();
    if (!specialties?.length) getSpecialties();
    if (valueFilter.length) {
      setSpecialtyList(findSpecialties(valueFilter));
      setShowSearchWrapper(true);
    } else setSpecialtyList(specialties);
    if (!selectedSpecialty) handleCleanSelectedFilters();

    setSpecialist(null);
    setSelectedSpecialistStore(undefined);
  }, []);

  const handleApplyFilters = (filters: Filters, isFilterVisible = false) => {
    setModalFilterVisible(isFilterVisible);

    setSpecialist(null);
    addSpecialistsStore([]);
    if (selectedSpecialty) getSpecialistsByFilter(selectedSpecialty, filters);
  };

  function resetSearch() {
    addSpecialistsStore([]);
    setSelectedSpecialistStore(undefined);
    setSpecialtyList(specialties);
    addFilterStore('');
    setSpecialist(null);
    setSpecialtyStore(undefined);
    setSelectedSpecialistStore(undefined);
  }

  return {
    resetSearch,
    handleApplyFilters,
    specialists,
    selectedSpecialist,
    handleClickListItem,
    getSpecialistsByName,
    specialist,
    specialtyList,
    showSearchWrapper,
    loading: loading > 0,
    modalFilterVisible,
    areaExpertises,
    medicalFields,
    changeContextSearch: setShowSearchWrapper,
    goTo,
    selectSpecialist: setSelectedSpecialistStore,
    valueFilter,
    selectedSpecialty,
    handleCleanSelectedFilters,
    setModalFilterVisible,
    selectedFilters,
    setSpecialist,
    setSpecialtyList,
    findSpecialties,
    addSpecialistsStore,
    specialties,
    listUnitsFilter,
    setSearchTherm
  };
};
